import { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { isEmpty } from 'lodash';
import { Button } from 'tuui';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { DragAndDrop } from './components/DragAndDrop';
import { GalleryArea } from './components/GalleryArea';
import { GenerateButton } from './components/GenerateButton';
import { MotionParamsDropdownContainer } from './components/MotionParamsDropdownsContainer';
import { MotionParamsPromptInput } from './components/MotionParamsPromptInput';
import { UploadedImage } from './components/UploadedImage';
import { UrlInput } from './components/UrlInput';
import { useFetchAIGeneratedMotion } from './hooks/useFetchAIGeneratedMotion';
import { useMotionParams } from './hooks/useMotionParams';
import styles from './imageToMotionSubTab.module.scss';

const classNameBuilder = classnames.bind(styles);

export const ImageToMotionSubTab = ({ onSelectMedia }) => {
    const { accountName } = useAccount();
    const [imageData, setImageData] = useState(null);
    const [videos, setVideos] = useState([]);
    const imageDataUrl = imageData?.value;
    const { clearMotionParamsFormFields, motionParams } = useMotionParams();

    const isMotionParamsEnabled = useConfigMatch({
        [FEATURE_FLAGS.IMAGE_TO_MOTION_PARAMS_ENABLED]: 'true',
    });

    const {
        error,
        isFetching,
        refetch: fetchAIGeneratedMotion,
    } = useFetchAIGeneratedMotion({
        imageUrl: imageDataUrl,
        accountId: accountName,
        isRegenerated: videos.length > 0,
        setVideos,
        ...(isMotionParamsEnabled && { motionParams }),
    });

    const onGenerateClick = async () => {
        if (!imageDataUrl || !accountName) {
            return;
        }
        await fetchAIGeneratedMotion();
    };

    const onCleanMedia = useCallback(() => {
        setImageData(null);
        setVideos([]);
    }, [setImageData, setVideos]);

    const handleClear = useCallback(() => {
        onCleanMedia();
        clearMotionParamsFormFields();
    }, [clearMotionParamsFormFields, onCleanMedia]);

    useEffect(() => {
        if (!imageDataUrl) {
            clearMotionParamsFormFields();
        }
    }, [clearMotionParamsFormFields, imageData, imageDataUrl]);

    const shouldShowClearButton =
        isMotionParamsEnabled && (!isEmpty(motionParams) || videos.length > 0 || !!imageDataUrl);

    const UploadedImageSection = (
        <>
            {isMotionParamsEnabled ? (
                <div className={styles['motion-params-section']} data-testid="motion-params-section">
                    <MotionParamsPromptInput imageDataUrl={imageDataUrl} onCleanMedia={onCleanMedia} />
                    <MotionParamsDropdownContainer />
                </div>
            ) : (
                <UploadedImage imageDataUrl={imageDataUrl} onCleanMedia={onCleanMedia} />
            )}
            <div className={classNameBuilder('footer-container', { isMotionParamsEnabled })}>
                {isMotionParamsEnabled && videos.length > 0 && (
                    <div className={styles['motion-generated-title']}>
                        <FormattedMessage
                            id="creative.editor.tab.aiGenerated.imageToMotion.generated.assets"
                            defaultMessage="Generated assets ({videosCount})"
                            values={{ videosCount: videos.length }}
                        />
                    </div>
                )}
                <div className={styles['buttons-container']}>
                    {shouldShowClearButton && (
                        <Button
                            size={Button.size.md}
                            variant={Button.variant.text}
                            onClick={handleClear}
                            className={styles['clear-button']}
                        >
                            <FormattedMessage
                                id="creative.editor.tab.aiGenerated.button.clear"
                                defaultMessage="Clear All"
                            />
                        </Button>
                    )}

                    <GenerateButton
                        onGenerateClick={onGenerateClick}
                        isLoading={isFetching}
                        isGalleryDisplayed={videos?.length > 0}
                    />
                </div>
            </div>
        </>
    );
    const InputSection = (
        <>
            <DragAndDrop setImageData={setImageData} />
            <UrlInput setImageData={setImageData} />
        </>
    );
    return (
        <div className={styles['image-to-motion-sub-tab']}>
            {imageDataUrl ? UploadedImageSection : InputSection}
            <GalleryArea
                isLoading={isFetching}
                shouldShowError={!!error}
                videos={videos}
                onSelectMedia={onSelectMedia}
            />
        </div>
    );
};
